/**
 * Utilidades para procesar la configuración de banners del ticker.
 *
 * Cada banner define un `move` (comportamiento visual), un `duration` en
 * segundos y un arreglo de imágenes. Aquí se normalizan esos datos y se
 * construye una lista aplanada y aleatorizada de "items" de imagen, donde
 * cada item conserva el `move` y `duration` de su banner de origen.
 */

/**
 * Modos de visualización soportados por cada banner.
 * @readonly
 */
export const BANNER_MOVE = {
  FIXED: "fijo",
  EXPAND_LEFT: "expandir izquierda",
  EXPAND_UP: "expandir arriba",
};

/**
 * Normaliza el valor del campo `move` a uno de los modos soportados.
 * Es tolerante a variaciones de texto (mayúsculas, "hacia", acentos, etc.).
 *
 * @param {string} rawMove
 * @returns {"fijo"|"expandir izquierda"|"expandir arriba"}
 */
export function normalizeMove(rawMove) {
  const value = String(rawMove ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // elimina acentos
    .trim();

  if (value.includes("izquierda")) return BANNER_MOVE.EXPAND_LEFT;
  if (value.includes("arriba")) return BANNER_MOVE.EXPAND_UP;
  return BANNER_MOVE.FIXED;
}

/**
 * Baraja un arreglo con el algoritmo Fisher-Yates (sin mutar el original).
 *
 * @template T
 * @param {T[]} array
 * @returns {T[]}
 */
export function shuffle(array) {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

/**
 * @typedef {Object} BannerImageItem
 * @property {string} bannerKey   Clave del banner de origen (ej. "banner1").
 * @property {string} move        Modo normalizado (fijo | expandir izquierda | expandir arriba).
 * @property {number} duration    Duración en segundos de esta imagen.
 * @property {number|null} width  Ancho del banner en px (según el JSON). null si no se define.
 * @property {number|null} height Alto del banner en px (según el JSON). null si no se define.
 * @property {string} url         URL de la imagen.
 * @property {string} name        Nombre/alt de la imagen.
 * @property {number} imageIndex  Índice de la imagen dentro de su banner.
 */

/**
 * Extrae el arreglo de banners de la respuesta del servicio.
 *
 * Soporta dos formatos:
 * - Nuevo: objeto `{ time_refresh, banners: [...] }` (el arreglo va en `banners`).
 * - Anterior: arreglo de banners directamente en la raíz.
 *
 * @param {Array|Object} data - Respuesta del servicio de banners.
 * @returns {Array} Arreglo de banners (vacío si no aplica).
 */
export function extractBannerArray(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object" && Array.isArray(data.banners)) return data.banners;
  return [];
}

/**
 * Obtiene el intervalo de recarga (en ms) definido por `time_refresh` (en
 * MINUTOS) del nuevo formato: `time_refresh * 60 * 1000`. Devuelve `null` si no
 * está presente o no es válido.
 *
 * @param {Array|Object} data - Respuesta del servicio de banners.
 * @returns {number|null} Intervalo en ms, o null si no aplica.
 */
export function getBannersRefreshMs(data) {
  const minutes = Number(data?.time_refresh);
  return minutes > 0 ? minutes * 60 * 1000 : null;
}

/**
 * Aplana la configuración de banners en una lista de items de imagen.
 *
 * @param {Array|Object} data - Respuesta del servicio de banners (nuevo formato
 *   `{ time_refresh, banners }` o arreglo plano anterior).
 * @returns {BannerImageItem[]}
 */
export function flattenBannerItems(data) {
  const banners = extractBannerArray(data);
  if (!banners.length) return [];

  const items = [];

  // Aplana una configuración de banner (duration/move/width/height/imagenes) en
  // items de imagen y los agrega a `items`. `bannerKey` identifica el banner de
  // origen (p. ej. su `type` o clave bannerN).
  const pushConfigItems = (config, bannerKey) => {
    if (!config || typeof config !== "object") return;

    const move = normalizeMove(config?.move);
    const duration = Number(config?.duration) > 0 ? Number(config.duration) : 0;
    const width = Number(config?.width) > 0 ? Number(config.width) : null;
    const height = Number(config?.height) > 0 ? Number(config.height) : null;
    // La estructura del servicio usa "imagenes" (se toleran variantes antiguas).
    const images = config?.imagenes ?? config?.imagnes ?? config?.images ?? [];

    if (!Array.isArray(images)) return;

    images.forEach((image, imageIndex) => {
      const url = image?.url ?? "";
      if (!url) return; // omite imágenes sin url

      items.push({
        bannerKey,
        move,
        duration,
        width,
        height,
        url,
        name: image?.name ?? "",
        imageIndex,
      });
    });
  };

  banners.forEach((bannerEntry, index) => {
    if (!bannerEntry || typeof bannerEntry !== "object") return;

    // Nuevo formato: cada banner es un objeto plano con `type` y su config
    // directamente (duration/move/width/height/imagenes).
    if (bannerEntry.type || bannerEntry.imagenes || bannerEntry.move) {
      pushConfigItems(bannerEntry, bannerEntry.type ?? `banner_${index + 1}`);
      return;
    }

    // Formato anterior: { bannerN: [ { config } ] }.
    Object.keys(bannerEntry).forEach((bannerKey) => {
      const configs = bannerEntry[bannerKey];
      if (!Array.isArray(configs)) return;
      configs.forEach((config) => pushConfigItems(config, bannerKey));
    });
  });

  return items;
}

/**
 * Evita que dos items consecutivos tengan `move = "expandir izquierda"`.
 *
 * Recorre la lista y, cuando encuentra dos "expandir izquierda" seguidos,
 * intercambia el segundo por el primer item posterior que no sea de ese modo.
 * No cambia el conjunto de items (todos siguen apareciendo una sola vez), solo
 * su orden. Si no es posible separarlos (p. ej. todos son de ese modo), la
 * lista se devuelve sin cambios en esas posiciones.
 *
 * @param {BannerImageItem[]} items - Lista ya aplanada/aleatorizada (se muta).
 * @returns {BannerImageItem[]}
 */
export function avoidConsecutiveExpandLeft(items) {
  for (let i = 1; i < items.length; i++) {
    const prevIsLeft = items[i - 1]?.move === BANNER_MOVE.EXPAND_LEFT;
    const currIsLeft = items[i]?.move === BANNER_MOVE.EXPAND_LEFT;

    if (prevIsLeft && currIsLeft) {
      // Busca hacia adelante el primer item que no sea "expandir izquierda".
      const swapIndex = items.findIndex(
        (item, index) => index > i && item?.move !== BANNER_MOVE.EXPAND_LEFT
      );

      if (swapIndex !== -1) {
        [items[i], items[swapIndex]] = [items[swapIndex], items[i]];
      }
      // Si no hay candidato, no se puede separar (todos los restantes son
      // "expandir izquierda"); se deja como está.
    }
  }

  return items;
}

/**
 * Construye la lista final de reproducción: aplana y aleatoriza todos los
 * items de imagen de todos los banners, garantizando que todos aparezcan y que
 * no se muestren dos "expandir izquierda" de forma consecutiva.
 *
 * @param {Array|Object} data - Respuesta del servicio (nuevo formato
 *   `{ time_refresh, banners }` o arreglo plano anterior).
 * @returns {BannerImageItem[]}
 */
export function buildBannerPlaylist(data) {
  return avoidConsecutiveExpandLeft(shuffle(flattenBannerItems(data)));
}

/**
 * Calcula el ancho (px) que ocupan los banners anclados a la derecha del ticker
 * ("fijo" y "expandir arriba"). Ese espacio queda por encima del contenido, por
 * lo que el carrusel del ticker debe reservarlo para que nada quede oculto.
 *
 * Se usa el ancho máximo entre esos banners (el peor caso de ocupación).
 * Los banners "expandir izquierda" no cuentan: son efímeros y ocupan el ancho
 * completo del contenedor, no reservan un área permanente a la derecha.
 *
 * @param {BannerImageItem[]} playlist - Playlist ya aplanada.
 * @returns {number} Ancho reservado en px (0 si no aplica).
 */
export function getReservedRightWidth(playlist) {
  if (!Array.isArray(playlist) || playlist.length === 0) return 0;

  return playlist.reduce((maxWidth, item) => {
    const anchoredRight = item?.move === BANNER_MOVE.FIXED || item?.move === BANNER_MOVE.EXPAND_UP;
    const width = anchoredRight && item?.width ? Number(item.width) : 0;
    return Math.max(maxWidth, width);
  }, 0);
}
